import { fromJS, List } from 'immutable';

import { toI18n } from 'util/i18n';

export const LAST_PUBLISHED_ID = 'LAST_PUBLISHED_ID';
export const DEFAULT_ROLE_COLOR = 'blue';

export const COLOR_VARIANTS = [
  'blue',
  'blue2',
  'fiolet',
  'fiolet2',
  'fiolet3',
  'fiolet4',
  'green',
  'green2',
  'green3',
  'orange',
  'orange2',
  'red',
  'red2',
  'yellow',
  'yellow2',
] as const;

export type Variant = (typeof COLOR_VARIANTS)[number];

export const INITIAL_PUBLISH_SCHEDULE_DRAWER_STATE = fromJS({
  data: null,
  schedulePublished: false,
  template: null,
  templateRecurrence: null,
  templateIsChanged: null,
  hasCreatedNote: false,
});

export const INITIAL_EMPTY_SCHEDULE_DRAWER_STATE = fromJS({
  date: null,
  lastPublishedSchedule: null,
  templates: List(),
  templatesIsFetching: false,
  hasBeenClosed: false,
});

export const INITIAL_SYNC_SHIFTS_DRAWER_STATE = fromJS({
  startDate: null,
  endDate: null,
  errorsCount: null,
});

export const INITIAL_SCHEDULE_BUILDER_STATE = fromJS({
  showScheduleSmsDiscoverability: false,
});

export const INITIAL_PRINT_DRAWER_STATE = fromJS({
  startDate: null,
  endDate: null,
  viewType: null,
});

export const REPEATING_TEMPLATES_FOREVER_OPTION = {
  value: -1,
  label: toI18n(
    'schedule_builder.publish_drawer.add_schedule_recurrence.repeating_template_options.forever'
  ),
};

export const REPEATING_TEMPLATES_OPTIONS = [1, 2, 3, 4, 10]
  .map(value => {
    const key =
      'schedule_builder.publish_drawer.add_schedule_recurrence.repeating_template_options.weeks';
    const label = toI18n(key, { props: { count: value } });

    return { value: value - 1, label };
  })
  .concat([REPEATING_TEMPLATES_FOREVER_OPTION]);

export const COLOR_PICKER_OPTIONS_V1: Variant[] = [
  'red2',
  'orange2',
  'orange',
  'yellow2',
  'red',
  'green',
  'green2',
  'blue',
  'green3',
  'fiolet',
  'yellow',
  'blue2',
];

export const COLOR_PICKER_OPTIONS_V2: Variant[] = [
  'red2',
  'orange2',
  'yellow',
  'yellow2',
  'red',
  'green',
  'blue',
  'blue2',
  'fiolet2',
  'fiolet',
  'fiolet4',
  'fiolet3',
];

export const COMMON_COLOR_PICKER_MAPPING: { [K in Variant]: string } = {
  red2: 'red',
  orange2: 'orange',
  orange: 'orange',
  yellow: 'gold',
  yellow2: 'yellow',
  red: 'salmon',
  green: 'green',
  green2: 'forest',
  green3: 'emerald',
  blue: 'teal',
  blue2: 'blue',
  fiolet2: 'indigo',
  fiolet: 'purple',
  fiolet4: 'magenta',
  fiolet3: 'dark purple',
};

export const COLOR_PICKER_MAPPING_V1: { [K in Variant]: string } = {
  ...COMMON_COLOR_PICKER_MAPPING,
  orange: 'gold',
  yellow: 'magenta',
  green2: 'teal',
  green3: 'indigo',
  blue: 'blue',
  blue2: 'dark purple',
};

export const COLOR_PICKER_MAPPING_V2: { [K in Variant]: string } = {
  ...COMMON_COLOR_PICKER_MAPPING,
};

export const COLORMAP_CLASSNAMES = {
  V1: 'option',
  V2: 'option-v2',
  ROLE_V1: 'role',
  ROLE_V2: 'role-v2',
};

const DEFAULT_COLOR_VALUE = 7;

export const DEFAULT_SHIFT_COLOR_V1 =
  COLOR_PICKER_OPTIONS_V1[DEFAULT_COLOR_VALUE];
export const DEFAULT_SHIFT_COLOR_V2 =
  COLOR_PICKER_OPTIONS_V2[DEFAULT_COLOR_VALUE];

export const DAYS_MAP = new Map([
  [1, 'M'],
  [2, 'T'],
  [3, 'W'],
  [4, 'T'],
  [5, 'F'],
  [6, 'S'],
  [0, 'S'],
]);

export const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const ROLE_PICKER_TRANSLATION_PATH =
  'schedule_builder.react_page.role_picker';

export const NO_ROLE_LABEL = toI18n(`${ROLE_PICKER_TRANSLATION_PATH}.no_role`);

export const CREATE_ROLE_LABEL = toI18n(
  `${ROLE_PICKER_TRANSLATION_PATH}.create_role`
);

export const TEAM_ROLE_LABEL = toI18n(
  `${ROLE_PICKER_TRANSLATION_PATH}.team_roles`
);

export const EMPLOYEE_ROLE_TYPE = 'employee';

export const TEAM_ROLE_TYPE = 'team';

export const INBUILT_ROLE_TYPE = 'inbuilt';

export const TEMPLATE_DETAILS_PATH = 'schedule_builder.react_page.template';

export type PARTNERS = 'toast';

export const SCHEDULE_BUILDER_PATH = '/schedule';

export const PREDICTION_TIMEOUT = 40000;

export const BASE_END_DELTA_DAYS = 6;

export const SB_ASSISTANT_TRIAL_END_DATE = new Date('2026-09-05');

export const SB_ASSISTANT_TRIAL_TIERS = ['essentials'];
